import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/core';
import { lowerCase, startCase } from 'lodash';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { number, bool, func, shape, arrayOf } from 'prop-types';

import { ToolTip } from '../../../components';
import { fetchKeyStoreList, resetAddKeyStore } from '../../../actions/keyStore';
import { getUserDetails } from '../../../reducers/portalConfig';
import { hasAdminRole, getConnectionStatusColor } from '../../../utils';
import {
  getKeyStoreListTotalPages,
  getKeyStoreListTotalElements,
  getKeyStoreListResults,
  getIsKeyStoreDeleteSuccess,
} from '../../../reducers/keyStore';
import styles from './styles';
import ListContainer from '../../list';
import {
  GRID_ROWS_PER_PAGE_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ALERT_SUCCESS,
  ALL,
  KEY_STORE_CONNECTED,
  KEY_STORE_TYPE_OTK,
} from '../../../constants';
import {
  FilterByName,
  FilterByDeploymentType,
  FilterAndSortSeparator,
  FilterByStatus,
  FilterByKeyStoreType,
} from './controls';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';

export const ListRaw = (props) => {
  const {
    classes,
    isLoading,
    userContext,
    totalPages,
    totalElements = 0,
    results = [],
    isKeyStoreDeleteSuccess,
  } = props;

  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [filterByName, setFilterByName] = useState('');
  const [filterByDeploymentType, setFilterByDeploymentType] = useState(ALL);
  const [filterByKeyStoreType, setFilterByKeyStoreType] = useState(ALL);
  const [filterByStatus, setFilterByStatus] = useState(ALL);
  const [filterByAnyApplied, setFilterByAnyApplied] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);

  const emptyFunction = () => {};

  useEffect(() => {
    if (!hasAdminRole(userContext)) { props.push('/404'); }
  }, [userContext]);

  useEffect(() => {
    props.fetchKeyStoreList({
      name: filterByName,
      deploymentType: filterByDeploymentType,
      keyStoreType: filterByKeyStoreType,
      status: filterByStatus,
      page: 0,
      size: rowsPerPage,
    });
  }, []);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    if (isKeyStoreDeleteSuccess) {
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.key.store.delete.success'),
      );
      setTimeout(() => { notifyMessages('', '')}, 2500);
    }
  }, [isKeyStoreDeleteSuccess]);

  const onFilterByNameChange = (value) => {
    setFilterByAnyApplied(true);
    setFilterByName(value);
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      props.fetchKeyStoreList({
        name: filterByName,
        deploymentType: filterByDeploymentType,
        keyStoreType: filterByKeyStoreType,
        status: filterByStatus,
        page: 0,
        size: rowsPerPage,
      });
    }
  };

  const onFilterByKeyStoreTypeChange = (value) => {
    setFilterByAnyApplied(true);
    setFilterByKeyStoreType(value);
    setPage(0);
    props.fetchKeyStoreList({
      name: filterByName,
      deploymentType: filterByDeploymentType,
      keyStoreType: value,
      status: filterByStatus,
      page: 0,
      size: rowsPerPage,
    });
  };

  const onFilterByDeploymentChange = (value) => {
    setFilterByAnyApplied(true);
    setFilterByDeploymentType(value);
    setPage(0);
    props.fetchKeyStoreList({
      name: filterByName,
      deploymentType: value,
      keyStoreType: filterByKeyStoreType,
      status: filterByStatus,
      page: 0,
      size: rowsPerPage,
    });
  };

  const onFilterByStatus = (value) => {
    setFilterByAnyApplied(true);
    setFilterByStatus(value);
    setPage(0);
    props.fetchKeyStoreList({
      name: filterByName,
      deploymentType: filterByDeploymentType,
      keyStoreType: filterByKeyStoreType,
      status: value,
      page: 0,
      size: rowsPerPage,
    });
  }

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    props.fetchKeyStoreList({
      name: filterByName,
      deploymentType: filterByDeploymentType,
      keyStoreType: filterByKeyStoreType,
      status: filterByStatus,
      page: newPage,
      size: rowsPerPage,
    });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    props.fetchKeyStoreList({
      name: filterByName,
      deploymentType: filterByDeploymentType,
      keyStoreType: filterByKeyStoreType,
      status: filterByStatus,
      page: 0,
      size: newRowsPerPage,
    });
  };

  const onAddKeyStore = () => {
    props.resetAddKeyStore();
    props.push('/admin/key-stores/details');
  };

  const noResultsMessage = filterByAnyApplied ?
    intl.getI18nMessage('label.key.store.filter.no.results') :
    intl.getI18nMessage('label.key.store.no.results');

  const LIST_COLUMNS = [{
    id: 'name',
    label: getI18nFormattedMessage('label.key.store'),
    minWidth: 100,
    link: '/admin/key-stores/details',
    },{
      id: 'keyStoreType',
      label: getI18nFormattedMessage('label.key.store.type'),
      minWidth: 100,
      format: value => {
        if (value === KEY_STORE_TYPE_OTK) {
          return KEY_STORE_TYPE_OTK;
        }
        return startCase(lowerCase(value));
      },
    },{
      id: 'deploymentType',
      label: getI18nFormattedMessage('label.key.store.deployment.type'),
      minWidth: 100,
      format: value => startCase(lowerCase(value)),
    },
    {
      id: 'connectionStatus',
      label: (
        <ToolTip
          classes={classes}
          label={getI18nFormattedMessage('label.key.store.connection.status')}
          info={<div>{getI18nFormattedMessage('label.key.store.connection.status.tool.tip')}</div>}
        />
      ),
      minWidth: 100,
      value: (item) => {
        const { connectionStatus } = item;
        const statusLabel = connectionStatus === KEY_STORE_CONNECTED
        ? startCase(lowerCase(connectionStatus)) : 'Disconnected'
        const statusIconStyle = {
          fill: getConnectionStatusColor(connectionStatus),
        };
        return (
          <span>
            <FiberManualRecordIcon size="0.82em" style={statusIconStyle} className={classes.statusIcon} />{statusLabel}
          </span>);
      },
    },
  ];

  return (
    <ListContainer
      listPageId="key-store-list"
      isLoading={isLoading}
      notificationId="key-store-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      pageHeaderTitle={intl.getI18nMessage('label.key.store.list.page.title')}
      pageHeaderDescription={intl.getI18nMessage('label.key.store.list.page.title.help.text')}
      pageHeaderTooltipTitle={''}
      addButtonLabel={intl.getI18nMessage('label.key.store.add.button')}
      onAdd={onAddKeyStore}
      showListHeader={true}
      filterAndSortContent={(
        <>
          <FilterByName
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByName}
            placeholder={intl.getI18nMessage('label.key.store')}
            handleChange={onFilterByNameChange}
            onKeyPress={onFilterByNameKeyPress}
          />
          <FilterByDeploymentType
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByDeploymentType}
            placeholder={intl.getI18nMessage('label.key.store.deployments.type.all')}
            handleChange={onFilterByDeploymentChange}
            hideLabel
          />
          <FilterByKeyStoreType
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByKeyStoreType}
            placeholder={intl.getI18nMessage('label.key.store.type.all')}
            handleChange={onFilterByKeyStoreTypeChange}
            hideLabel
          />
           <FilterByStatus
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByStatus}
            placeholder={intl.getI18nMessage('label.key.store.status')}
            handleChange={onFilterByStatus}
            hideLabel
          />
          <FilterAndSortSeparator />
        </>
     )}
      columns={LIST_COLUMNS}
      rows={results}
      noResultsMessage={noResultsMessage}
      page={page}
      totalElements={totalElements}
      totalPages={totalPages}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
      notifyMessages={emptyFunction}
    />
  );
};

ListRaw.propTypes = {
  classes: shape({}),
  userContext: shape({}),
  isLoading: bool,
  totalPages: number,
  totalElements: number,
  results: arrayOf(shape({})),
  fetchKeyStoreList: func,
  resetApp: func,
  notifyMessages: func,
  push: func,
  isKeyStoreDeleteSuccess: bool,
  resetAddKeyStore: func,
};

const mapStateToProps = (state) => ({
  isLoading: false,
  totalPages: getKeyStoreListTotalPages(state),
  totalElements: getKeyStoreListTotalElements(state),
  results: getKeyStoreListResults(state),
  userContext: getUserDetails(state),
  isKeyStoreDeleteSuccess: getIsKeyStoreDeleteSuccess(state),
});

const mapDispatchToProps = {
  fetchKeyStoreList,
  resetAddKeyStore,
  push,
};
ListRaw.displayName = 'KeyStores';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ListRaw);
